import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, FileSpreadsheet } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">PDF STATEMENT CONVERTER</span>
          </div>
          <Link 
            to="/" 
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-ZA')}
            </p>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-600 mb-4">
                PDF Statement Converter ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our bank statement conversion service.
              </p>
              <p className="text-gray-600">
                By using our service, you agree to the collection and use of information in accordance 
                with this policy. This policy complies with the Protection of Personal Information Act 
                (POPIA) and other applicable South African data protection laws.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Personal Information</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li>Email address and name for account creation</li>
                <li>Payment information processed through Paystack</li>
                <li>Usage analytics and service interaction data</li>
                <li>Technical information such as IP address, browser type, and device information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Bank Statement Data</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li>PDF bank statements you upload for conversion</li>
                <li>Transaction data extracted from your statements</li>
                <li>Account numbers and bank details contained in statements</li>
                <li>Financial transaction history and amounts</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.3 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Log data including access times and actions performed</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Service usage patterns and performance metrics</li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Service Provision</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li>Convert your PDF bank statements to CSV format</li>
                <li>Process payments and manage your credit balance</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Maintain and improve our service quality</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Legal and Business Purposes</h3>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Comply with legal obligations and regulatory requirements</li>
                <li>Detect and prevent fraud or unauthorized access</li>
                <li>Enforce our Terms of Service</li>
                <li>Analyze service usage to improve functionality</li>
              </ul>
            </section>

            {/* Data Storage and Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Storage and Security</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Data Security</h3>
              <p className="text-gray-600 mb-4">
                We implement bank-grade security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li>End-to-end encryption for all data transmission</li>
                <li>Secure cloud storage with Firebase/Google Cloud Platform</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication systems</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Data Retention</h3>
              <p className="text-gray-600 mb-4">
                <strong>Automatic File Deletion:</strong> All uploaded PDF files and converted CSV files 
                are automatically deleted from our servers after 24 hours.
              </p>
              <p className="text-gray-600">
                <strong>Account Data:</strong> We retain your account information and transaction history 
                for as long as your account remains active, plus 7 years for financial record-keeping 
                requirements.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Services</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Payment Processing</h3>
              <p className="text-gray-600 mb-4">
                We use Paystack to process payments. Paystack has access to payment information 
                necessary to complete transactions. Please review Paystack's privacy policy for 
                details on their data handling practices.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Cloud Infrastructure</h3>
              <p className="text-gray-600 mb-4">
                Our service is hosted on Google Cloud Platform through Firebase services. 
                Google may have access to certain data as outlined in their privacy policies.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Analytics</h3>
              <p className="text-gray-600">
                We use analytics services to understand how our service is used and to improve 
                user experience. This data is anonymized and aggregated.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.4 Processing</h3>
              <p className="text-gray-600">
                We use partner services in order to process files. Documents uploaded persist on their servers for 24 hours. After 24 hours they are deleted. 
              </p>

            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights Under POPIA</h2>
              <p className="text-gray-600 mb-4">
                Under the Protection of Personal Information Act, you have the following rights:
              </p>
              <ul className="list-disc pl-6 text-gray-600">
                <li><strong>Right to Access:</strong> Request copies of your personal information</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing of your personal information</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Sharing and Disclosure</h2>
              <p className="text-gray-600 mb-4">
                We do not sell, trade, or rent your personal information to third parties. 
                We may share information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-600">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations or court orders</li>
                <li>To protect our rights, property, or safety</li>
                <li>In connection with a business transfer or acquisition</li>
                <li>With service providers who assist in our operations (under strict confidentiality)</li>
              </ul>
            </section>

            {/* Cookies and Tracking */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="text-gray-600 mb-4">
                We use cookies and similar technologies to enhance your experience and analyze 
                service usage. You can control cookie settings through your browser preferences.
              </p>
              <p className="text-gray-600">
                Essential cookies are necessary for service functionality, while analytics cookies 
                help us improve our service. You can opt out of non-essential cookies without 
                affecting core functionality.
              </p>
            </section>

            {/* International Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
              <p className="text-gray-600">
                Your data may be stored and processed on servers located outside South Africa, 
                particularly through our use of Google Cloud Platform. We ensure adequate 
                protection measures are in place for international transfers as required by POPIA.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-600">
                Our service is not intended for individuals under 18 years of age. We do not 
                knowingly collect personal information from children under 18. If you become 
                aware that a child has provided us with personal information, please contact us 
                so we can take appropriate action.
              </p>
            </section>

            {/* Policy Updates */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Privacy Policy Updates</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy from time to time. We will notify you of any 
                material changes by posting the new Privacy Policy on this page and updating the 
                "Effective Date" at the top. Your continued use of our service after changes 
                constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about this Privacy Policy or wish to exercise your 
                rights under POPIA, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-2">
                  <strong>Email:</strong> admin@pdfstatementconverter.com
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Data Protection Officer:</strong> admin@pdfstatementconverter.com
                </p>
                <p className="text-gray-600">
                  <strong>Response Time:</strong> We will respond to privacy requests within 30 days
                </p>
              </div>
            </section>

            {/* Information Regulator */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Information Regulator</h2>
              <p className="text-gray-600 mb-4">
                If you are not satisfied with our response to your privacy concerns, you have 
                the right to lodge a complaint with the Information Regulator of South Africa:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-2">
                  <strong>Website:</strong> www.inforegulator.org.za
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Email:</strong> complaints.IR@justice.gov.za
                </p>
                <p className="text-gray-600">
                  <strong>Phone:</strong> 012 406 4818
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">
            © {new Date().getFullYear()} PDF Statement Converter. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;